import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { capture } from '../analytics';
import { tourMode, endTour, retireForPaid, visibleRect, placeCard } from '../onboarding/tour';

/*
 * The first-run tour for new free members (rules in onboarding/tour.js).
 *
 * Each step points at something on screen through its data-tour attribute and
 * says what it is for. A target that is not on screen (a phone has no top
 * menu) gets its card in the middle instead; the phone has its own shorter
 * route through the menu button. Nothing is opened or clicked for the member:
 * the tour only points.
 */

const DESKTOP = ['welcome', 'nav-resources', 'nav-projects', 'nav-labs', 'nav-paths', 'nav-jobs', 'search', 'sidebar', 'user-menu', 'finish'];
const MOBILE = ['welcome', 'menu-button', 'user-menu', 'finish'];

function useSteps(t, firstName) {
  return useMemo(() => ({
    welcome: {
      title: t('tour.welcomeTitle', { defaultValue: 'Welcome to LearnXops, {{name}} 👋', name: firstName }),
      body: t('tour.welcomeBody', 'Take a one-minute look around, so you know where everything is.'),
    },
    'nav-resources': {
      title: t('tour.resourcesTitle', 'Resources'),
      body: t('tour.resourcesBody', 'Articles, e-books, system designs and career roadmaps, written by engineers who do this work every day.'),
    },
    'nav-projects': {
      title: t('tour.projectsTitle', 'Projects'),
      body: t('tour.projectsBody', 'Real Cloud, DevOps, MLOps and AI projects to build step by step and add to your portfolio.'),
    },
    'nav-labs': {
      title: t('tour.labsTitle', 'Labs'),
      body: t('tour.labsBody', 'Hands-on labs that run in your browser: Kubernetes, local cloud and AI agents. Finish one and earn a certificate you can share.'),
    },
    'nav-paths': {
      title: t('tour.pathsTitle', 'Learning Paths'),
      body: t('tour.pathsBody', 'Day-by-day roadmaps that take you through a topic from the basics to job-ready.'),
    },
    'nav-jobs': {
      title: t('tour.jobsTitle', 'Jobs & Interviews'),
      body: t('tour.jobsBody', 'Curated jobs, an application tracker, résumé and cover letter builders, interview Q&A and mock interviews.'),
    },
    search: {
      title: t('tour.searchTitle', 'Search everything'),
      body: t('tour.searchBody', 'Find any article, lab or project in seconds. Press ⌘K (Ctrl+K on Windows) from anywhere.'),
    },
    sidebar: {
      title: t('tour.sidebarTitle', 'Your shortcuts'),
      body: t('tour.sidebarBody', 'Your dashboard, your learning, the AI tools and the community are always one click away here.'),
    },
    'menu-button': {
      title: t('tour.menuTitle', 'Everything is in the menu'),
      body: t('tour.menuBody', 'Articles, projects, labs, learning paths, jobs and interview prep — open the menu to find them.'),
    },
    'user-menu': {
      title: t('tour.profileTitle', 'Your account'),
      body: t('tour.profileBody', 'Your profile, membership, AI provider, language and theme live here.'),
    },
    finish: {
      title: t('tour.finishTitle', "You're all set 🎉"),
      body: t('tour.finishBody', 'A good first step: start a hands-on lab. Upgrade to Pro any time to unlock every lab, the AI tools and the Job Agent.'),
    },
  }), [t, firstName]);
}

const CARD_W = 340;

export default function WelcomeTour() {
  const { t } = useTranslation();
  const { user, isPaid, shadow } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState(null);
  const [index, setIndex] = useState(0);
  const [route, setRoute] = useState(DESKTOP);
  const [view, setView] = useState({ rect: null, pos: null });
  const cardRef = useRef(null);
  const nextRef = useRef(null);
  const firstName = String(user?.name || '').trim().split(/\s+/)[0] || t('tour.there', 'there');
  const steps = useSteps(t, firstName);

  // Decide once per sign-in whether to run; a moment's delay lets the page draw first.
  useEffect(() => {
    const m = tourMode({ user, isPaid, shadow, search: location.search });
    if (!shadow) retireForPaid({ user, isPaid });
    if (!m) { setMode(null); return undefined; }
    const timer = setTimeout(() => {
      const desktop = !!visibleRect(document.querySelector('[data-tour="nav-resources"]'), { w: window.innerWidth, h: window.innerHeight });
      setRoute(desktop ? DESKTOP : MOBILE);
      setIndex(0);
      setMode(m);
      capture('tour_started', { preview: m === 'preview' });
    }, 900);
    return () => clearTimeout(timer);
    // location.search only matters for ?tour=preview on arrival
  }, [user?.id, isPaid, shadow]); // eslint-disable-line react-hooks/exhaustive-deps

  const id = route[index];
  const step = steps[id];

  // Follow the target as the window scrolls or resizes.
  const measure = useCallback(() => {
    if (!mode) return;
    const viewport = { w: window.innerWidth, h: window.innerHeight };
    const rect = id === 'welcome' || id === 'finish' ? null : visibleRect(document.querySelector(`[data-tour="${id}"]`), viewport);
    const card = { w: Math.min(CARD_W, viewport.w - 32), h: cardRef.current?.offsetHeight || 220 };
    setView({ rect, pos: placeCard(rect, card, viewport), cardW: card.w });
  }, [mode, id]);

  useLayoutEffect(() => { measure(); }, [measure, index]);
  useEffect(() => {
    if (!mode) return undefined;
    let frame = 0;
    const on = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure); };
    window.addEventListener('resize', on);
    window.addEventListener('scroll', on, true);
    return () => { cancelAnimationFrame(frame); window.removeEventListener('resize', on); window.removeEventListener('scroll', on, true); };
  }, [mode, measure]);

  const close = useCallback((outcome, then) => {
    endTour(user, mode, outcome);
    capture(outcome === 'completed' ? 'tour_completed' : 'tour_skipped', { step: id, preview: mode === 'preview' });
    setMode(null);
    if (then) navigate(then);
  }, [user, mode, id, navigate]);

  const next = useCallback(() => (index < route.length - 1 ? setIndex(index + 1) : close('completed')), [index, route.length, close]);
  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  useEffect(() => {
    if (!mode) return undefined;
    nextRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') close('skipped');
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, index, close, next, back]);

  if (!mode || !step) return null;
  const { rect, pos, cardW } = view;
  const last = index === route.length - 1;
  const pad = 6;

  return (
    <div className="fixed inset-0 z-[200]" role="dialog" aria-modal="true" aria-labelledby="lx-tour-title">
      {/* The page dims; the step's target shows through a cut-out. Clicks stay with the tour. */}
      {rect ? (
        <div className="absolute rounded-xl pointer-events-none transition-all duration-200"
          style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2,
            boxShadow: '0 0 0 9999px rgba(3,7,18,0.72), 0 0 0 2px rgba(129,140,248,0.9)' }} />
      ) : (
        <div className="absolute inset-0" style={{ background: 'rgba(3,7,18,0.72)' }} />
      )}

      <div ref={cardRef}
        className="absolute rounded-2xl p-5 shadow-2xl transition-all duration-200"
        style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: cardW || CARD_W,
          background: 'var(--lx-surface, #111827)', border: '1px solid var(--lx-border, #1f2937)' }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-indigo-300">
            {mode === 'preview' ? t('tour.preview', 'Tour preview') : t('tour.label', 'Quick tour')} · {index + 1}/{route.length}
          </span>
          <button type="button" onClick={() => close('skipped')} className="text-gray-500 hover:text-white text-sm" aria-label={t('tour.skip', 'Skip tour')}>✕</button>
        </div>
        <h2 id="lx-tour-title" className="text-white font-semibold text-base mb-1.5">{step.title}</h2>
        <p className="text-sm text-gray-400 leading-relaxed">{step.body}</p>

        <div className="flex items-center gap-1.5 mt-4" aria-hidden="true">
          {route.map((s, i) => (
            <span key={s} className="h-1.5 rounded-full transition-all"
              style={{ width: i === index ? 18 : 6, background: i <= index ? '#818cf8' : 'rgba(148,163,184,0.3)' }} />
          ))}
        </div>

        {last ? (
          <div className="flex flex-wrap gap-2 mt-4">
            <button ref={nextRef} type="button" onClick={() => close('completed', '/labs')}
              className="px-4 py-2 rounded-xl text-sm font-semibold text-white" style={{ background: 'linear-gradient(135deg,#6366f1,#7c3aed)' }}>
              {t('tour.startLab', 'Start a lab')}
            </button>
            <button type="button" onClick={() => close('completed', '/dashboard')}
              className="px-4 py-2 rounded-xl text-sm text-gray-300" style={{ border: '1px solid var(--lx-border, #1f2937)' }}>
              {t('tour.dashboard', 'Go to my dashboard')}
            </button>
            <button type="button" onClick={() => close('completed', '/membership')} className="px-2 py-2 text-sm text-indigo-300 hover:text-indigo-200">
              {t('tour.seePro', 'See Pro')}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between mt-4">
            <button type="button" onClick={() => close('skipped')} className="text-sm text-gray-500 hover:text-gray-300">
              {index === 0 ? t('tour.notNow', 'Not now') : t('tour.skip', 'Skip tour')}
            </button>
            <div className="flex gap-2">
              {index > 0 && (
                <button type="button" onClick={back} className="px-3 py-1.5 rounded-lg text-sm text-gray-300" style={{ border: '1px solid var(--lx-border, #1f2937)' }}>
                  {t('tour.back', 'Back')}
                </button>
              )}
              <button ref={nextRef} type="button" onClick={next}
                className="px-4 py-1.5 rounded-lg text-sm font-semibold text-white" style={{ background: '#6366f1' }}>
                {index === 0 ? t('tour.start', 'Show me around') : t('tour.next', 'Next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
